import { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { 
  X, 
  Plus, 
  Trash2, 
  AlertTriangle, 
  Calendar,
  Save,
  FileText,
  Send,
  Copy
} from 'lucide-react';
import { format } from 'date-fns';

// Check if two time ranges overlap
function timesOverlap(start1, end1, start2, end2) {
  const toMinutes = (t) => {
    if (!t) return 0;
    const [h, m] = t.split(':').map(Number);
    return h * 60 + m;
  };
  
  let s1 = toMinutes(start1);
  let e1 = toMinutes(end1);
  let s2 = toMinutes(start2);
  let e2 = toMinutes(end2);
  
  if (e1 < s1) e1 += 24 * 60;
  if (e2 < s2) e2 += 24 * 60;
  
  return s1 < e2 && s2 < e1;
}

// Get contractor availability status
function getContractorStatus(contractorId, date, startTime, endTime, availability) {
  if (!date || !availability || !availability[contractorId]) {
    return { status: 'available' };
  }
  
  const dateStr = typeof date === 'string' ? date : format(date, 'yyyy-MM-dd');
  const contractorAvail = availability[contractorId];
  
  // Check unavailable
  const unavail = contractorAvail.unavailable?.find(u => u.date === dateStr);
  if (unavail) {
    if (unavail.allDay) {
      return { status: 'unavailable', detail: unavail.reason || 'Unavailable' };
    }
    if (startTime && unavail.startTime && unavail.endTime) {
      if (timesOverlap(startTime, endTime || '23:59', unavail.startTime, unavail.endTime)) {
        return { status: 'unavailable', detail: `Unavail ${unavail.startTime}-${unavail.endTime}` };
      }
    }
  }
  
  // Check booked
  const bookings = contractorAvail.booked?.filter(b => b.date === dateStr) || [];
  for (const booking of bookings) {
    if (startTime && booking.startTime && booking.endTime) {
      if (timesOverlap(startTime, endTime || '23:59', booking.startTime, booking.endTime)) {
        return { status: 'booked', detail: `${booking.location} ${booking.startTime}` };
      }
    }
  }
  
  return { status: 'available' };
}

// Single row component
function GigRow({ 
  row, 
  index, 
  onChange, 
  onRemove, 
  onDuplicate,
  hierarchy,
  contractors, 
  roles,
  availability
}) {
  const contractorStatus = row.contractorId 
    ? getContractorStatus(row.contractorId, row.date, row.startTime, row.endTime, availability)
    : { status: 'available' };

  // Build flat location list for dropdown
  const locationOptions = [];
  hierarchy?.forEach(client => {
    client.venues?.forEach(venue => {
      venue.locations?.forEach(loc => {
        locationOptions.push({
          id: loc.id,
          label: `${client.name} → ${venue.name} → ${loc.name}`,
          clientName: client.name,
          venueName: venue.name,
          locationName: loc.name,
          defaultClientBilled: loc.defaultClientBilled,
          defaultPayout: loc.defaultPayout
        });
      });
    });
  });

  const handleLocationChange = (locationId) => {
    const loc = locationOptions.find(l => l.id === locationId);
    onChange(index, { 
      ...row, 
      locationId,
      // Auto-fill billing if empty
      clientBilled: row.clientBilled || (loc?.defaultClientBilled?.toString() || ''),
      payout: row.payout || (loc?.defaultPayout?.toString() || '')
    });
  };

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50 group">
      {/* Date */}
      <td className="py-2 px-1">
        <input
          type="date"
          value={row.date}
          onChange={(e) => onChange(index, { ...row, date: e.target.value })}
          className="input text-sm py-1.5 w-full"
        />
      </td>
      
      {/* Start Time */}
      <td className="py-2 px-1">
        <input
          type="time"
          value={row.startTime}
          onChange={(e) => onChange(index, { ...row, startTime: e.target.value })}
          className="input text-sm py-1.5 w-full"
        />
      </td>
      
      {/* End Time */}
      <td className="py-2 px-1">
        <input
          type="time"
          value={row.endTime}
          onChange={(e) => onChange(index, { ...row, endTime: e.target.value })}
          className="input text-sm py-1.5 w-full"
        />
      </td>
      
      {/* Location */}
      <td className="py-2 px-1">
        <select
          value={row.locationId}
          onChange={(e) => handleLocationChange(e.target.value)}
          className="input text-sm py-1.5 w-full"
        >
          <option value="">Select location...</option>
          {hierarchy?.map(client => (
            <optgroup key={client.id} label={client.name}>
              {client.venues?.map(venue => (
                venue.locations?.map(loc => (
                  <option key={loc.id} value={loc.id}>
                    {venue.name} → {loc.name}
                  </option>
                ))
              ))}
            </optgroup>
          ))}
        </select>
      </td>
      
      {/* Client Billed */}
      <td className="py-2 px-1">
        <div className="relative">
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
          <input
            type="number"
            value={row.clientBilled}
            onChange={(e) => onChange(index, { ...row, clientBilled: e.target.value })}
            className="input text-sm py-1.5 w-full pl-5"
            placeholder="0"
            min="0"
          />
        </div>
      </td>
      
      {/* Contractor */}
      <td className="py-2 px-1">
        <select
          value={row.contractorId}
          onChange={(e) => onChange(index, { ...row, contractorId: e.target.value })}
          className={`input text-sm py-1.5 w-full ${
            contractorStatus.status === 'booked' ? 'border-amber-400 bg-amber-50' :
            contractorStatus.status === 'unavailable' ? 'border-red-300 bg-red-50' : ''
          }`}
        >
          <option value="">None</option>
          {contractors.map(c => {
            const status = getContractorStatus(c.id, row.date, row.startTime, row.endTime, availability);
            return (
              <option key={c.id} value={c.id}>
                {status.status === 'unavailable' ? '✖ ' : status.status === 'booked' ? '⚠ ' : ''}
                {c.name}
              </option>
            );
          })}
        </select>
        {contractorStatus.status !== 'available' && (
          <div className={`text-xs mt-0.5 ${
            contractorStatus.status === 'booked' ? 'text-amber-600' : 'text-red-500'
          }`}>
            {contractorStatus.status === 'booked' ? '⚠' : '✖'} {contractorStatus.detail}
          </div>
        )}
      </td>
      
      {/* Role */}
      <td className="py-2 px-1">
        <input
          type="text"
          value={row.role}
          onChange={(e) => onChange(index, { ...row, role: e.target.value })}
          className="input text-sm py-1.5 w-full"
          placeholder="DJ"
          list="bulk-roles"
        />
      </td>
      
      {/* Payout */}
      <td className="py-2 px-1">
        <div className="relative">
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">$</span>
          <input
            type="number"
            value={row.payout}
            onChange={(e) => onChange(index, { ...row, payout: e.target.value })}
            className="input text-sm py-1.5 w-full pl-5"
            placeholder="0"
            min="0"
          />
        </div>
      </td>
      
      {/* Actions */}
      <td className="py-2 px-1">
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            onClick={() => onDuplicate(index)}
            className="p-1 text-gray-400 hover:text-hub-600 hover:bg-hub-50 rounded"
            title="Duplicate row"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
            title="Remove row"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function BulkGigModal({ isOpen, onClose, onCreated, hierarchy }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Rows - each is a complete gig
  const [rows, setRows] = useState([
    createEmptyRow()
  ]);
  
  // Reference data
  const [contractors, setContractors] = useState([]);
  const [roles, setRoles] = useState([]);
  const [availability, setAvailability] = useState({});
  const [templates, setTemplates] = useState([]);
  
  function createEmptyRow() {
    return {
      date: format(new Date(), 'yyyy-MM-dd'),
      startTime: '20:00',
      endTime: '00:00',
      locationId: '',
      clientBilled: '',
      contractorId: '',
      role: 'DJ',
      payout: ''
    };
  }
  
  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);
  
  // Load availability when dates change
  useEffect(() => {
    if (isOpen && rows.length > 0) {
      const dates = [...new Set(rows.map(r => r.date).filter(Boolean))];
      if (dates.length > 0) {
        loadAvailability(dates);
      }
    }
  }, [rows.map(r => r.date).join(','), isOpen]);
  
  const loadData = async () => {
    try {
      const [contractorsData, rolesData, templatesData] = await Promise.all([
        api.get('/contractors?active=true'),
        api.get('/roles'),
        api.get('/templates')
      ]);
      setContractors(contractorsData);
      setRoles(rolesData);
      setTemplates(templatesData || []);
    } catch (err) {
      console.error('Failed to load data:', err);
    }
  };
  
  const loadAvailability = async (dates) => {
    try {
      const data = await api.post('/contractors/availability-check', { dates });
      setAvailability(data);
    } catch (err) {
      console.error('Failed to load availability:', err);
    }
  };
  
  const addRow = () => {
    const lastRow = rows[rows.length - 1];
    setRows([...rows, {
      ...createEmptyRow(),
      // Copy some fields from last row for convenience
      startTime: lastRow?.startTime || '20:00',
      endTime: lastRow?.endTime || '00:00',
      role: lastRow?.role || 'DJ'
    }]);
  };
  
  const duplicateRow = (index) => {
    const row = rows[index];
    const newRows = [...rows];
    newRows.splice(index + 1, 0, { ...row });
    setRows(newRows);
  };

  const addRowFromTemplate = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return;
    
    const lastRow = rows[rows.length - 1];
    const newRow = {
      date: lastRow?.date || format(new Date(), 'yyyy-MM-dd'),
      startTime: template.startTime || '20:00',
      endTime: template.endTime || '00:00',
      locationId: template.locationId || '',
      clientBilled: template.clientBilled?.toString() || '',
      contractorId: '',
      role: template.defaultRole || 'DJ',
      payout: template.defaultPayout?.toString() || ''
    };
    
    // If the only row is empty, replace it. Otherwise add new row.
    if (rows.length === 1 && !rows[0].locationId) {
      setRows([newRow]);
    } else {
      setRows([...rows, newRow]);
    }
  };
  
  const updateRow = (index, updated) => {
    const newRows = [...rows];
    newRows[index] = updated;
    setRows(newRows);
  };
  
  const removeRow = (index) => {
    if (rows.length <= 1) {
      setRows([createEmptyRow()]);
      return;
    }
    setRows(rows.filter((_, i) => i !== index));
  };
  
  const handleSubmit = async (asDraft = false) => {
    // Validate
    const validRows = rows.filter(r => r.locationId && r.date);
    
    if (validRows.length === 0) {
      setError('Please add at least one gig with a location and date');
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      const gigs = validRows.map(row => ({
        locationId: row.locationId,
        date: row.date,
        startTime: row.startTime,
        endTime: row.endTime,
        clientBilled: parseFloat(row.clientBilled) || 0,
        status: asDraft ? 'DRAFT' : 'PENDING',
        // Backend expects contractorId, role, payout at top level, not nested in assignments
        contractorId: row.contractorId || null,
        role: row.role || null,
        payout: parseFloat(row.payout) || 0
      }));
      
      const result = await api.post('/gigs/bulk', { 
        gigs, 
        saveAsDrafts: asDraft 
      });
      
      onCreated(result);
      onClose();
      
      // Reset form
      setRows([createEmptyRow()]);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };
  
  if (!isOpen) return null;
  
  const validCount = rows.filter(r => r.locationId && r.date).length;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div className="flex items-center gap-3">
            <Calendar className="w-6 h-6 text-hub-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Add Multiple Gigs</h2>
              <p className="text-sm text-gray-500">{rows.length} row{rows.length !== 1 ? 's' : ''} • {validCount} valid</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-auto p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}
          
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px]">
              <thead className="bg-gray-50 text-xs text-gray-600 uppercase sticky top-0">
                <tr>
                  <th className="py-2 px-1 text-left font-medium w-32">Date</th>
                  <th className="py-2 px-1 text-left font-medium w-24">Start</th>
                  <th className="py-2 px-1 text-left font-medium w-24">End</th>
                  <th className="py-2 px-1 text-left font-medium min-w-[200px]">Location</th>
                  <th className="py-2 px-1 text-left font-medium w-24">Billed</th>
                  <th className="py-2 px-1 text-left font-medium w-40">Contractor</th>
                  <th className="py-2 px-1 text-left font-medium w-20">Role</th>
                  <th className="py-2 px-1 text-left font-medium w-24">Payout</th>
                  <th className="py-2 px-1 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <GigRow
                    key={index}
                    row={row}
                    index={index}
                    onChange={updateRow}
                    onRemove={removeRow}
                    onDuplicate={duplicateRow}
                    hierarchy={hierarchy}
                    contractors={contractors}
                    roles={roles}
                    availability={availability}
                  />
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={addRow}
              className="flex-1 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-hub-400 hover:text-hub-600 transition-colors flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Row
            </button>
            
            {templates.length > 0 && (
              <div className="flex-1 relative">
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      addRowFromTemplate(e.target.value);
                      e.target.value = '';
                    }
                  }}
                  className="w-full py-2 px-3 border-2 border-dashed border-hub-300 rounded-lg text-hub-600 bg-hub-50 hover:border-hub-400 cursor-pointer appearance-none text-center font-medium"
                  defaultValue=""
                >
                  <option value="">+ Add from Template</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          
          <datalist id="bulk-roles">
            {roles.map(r => <option key={r} value={r} />)}
          </datalist>
          
          {/* Legend */}
          <div className="mt-4 flex items-center gap-4 text-xs text-gray-500">
            <span>Contractor availability:</span>
            <span>No indicator = Available</span>
            <span className="text-amber-600">⚠ = Already booked</span>
            <span className="text-red-500">✖ = Unavailable</span>
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <button type="button" onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          
          <div className="flex items-center gap-2">
            <button 
              onClick={() => handleSubmit(true)}
              className="btn-secondary flex items-center gap-2"
              disabled={saving || validCount === 0}
            >
              <FileText className="w-4 h-4" />
              Save as Drafts
            </button>
            <button 
              onClick={() => handleSubmit(false)}
              className="btn-primary flex items-center gap-2"
              disabled={saving || validCount === 0}
            >
              <Send className="w-4 h-4" />
              {saving ? 'Creating...' : `Save & Publish ${validCount} Gig${validCount !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
